import {
  Box,
  Button,
  List,
  ListItemIcon,
  ListSubheader,
  styled,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  PlanningTask,
  RouteEntity,
  ScheduleTask,
  TaskListCategory,
  TaskListProps
} from './types';
import { useTranslation } from 'react-i18next';
import FontAwesomeSvgIcon from '../FontAwesomeSvgIcon';
import {
  faChevronDoubleLeft,
  faChevronDoubleRight
} from 'vendors/@fortawesome/pro-solid-svg-icons';
import {
  GRAY,
  NEW_GREEN_LIGHT,
  ORANGE_LIGHT,
  rgbaPrimary
} from 'constants/colors';
import { useSelector } from 'hooks';
import ToPlanDropzone from 'components/roster-portal/activity-planning/components/InnerList/ToPlanDropzone';
import TaskListActionBar from './components/TaskListActionBar';
import TaskListWrapper from './components/TaskListWrapper';
import { PlanningEntity } from 'components/roster-portal/activity-planning/types';
import { toPlanningTasksWithDropzones } from 'components/roster-portal/activity-planning/utils';
import { EventResponse } from 'generated/index';
import { CategoryBadgeWithTooltip } from './components/CategoryBadgeWithTooltip';

export const TASK_LIST_WIDTH = 320;

const Header = styled(ListSubheader)`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: transparent;
  border-bottom: 1px solid ${GRAY};
  padding-top: 12px;
  padding-bottom: 20px;
  margin-bottom: 8px;
`;

const IconButton = styled(Button)`
  position: fixed;
  right: 0px;
  min-width: inherit;
  width: 40px;
  height: 40px;
  border-radius: 50%;

  > * {
    min-width: 0;
    svg {
      min-width: inherit;
    }
  }
`;

const TaskList = ({
  onOpen,
  onClose,
  planningEntities,
  dropzonePadding,
  activeEntityContainingSelection,
  onChangeActiveEntity,
  onChangeSelection,
  onChangeMousePosition,
  onRouteClick,
  isTimeline = false
}: TaskListProps) => {
  const planningEntitiesArr: PlanningEntity[] = Object.values(
    planningEntities || {}
  );

  // Get all planning entities of type 'ORGANIZATIONAL_UNIT_TO_PLAN'
  const planningEntitiesToPlan: PlanningEntity[] = planningEntitiesArr.filter(
    ({ type }) => type === 'ORGANIZATIONAL_UNIT_TO_PLAN'
  );

  const planEventsPerEntity = planningEntitiesToPlan.map((planningEntity) => {
    const planEvents =
      toPlanningTasksWithDropzones(
        (planningEntity?.planningTasks as PlanningTask[]) ?? []
      )
        ?.filter((task) => task.category === 'APPOINTMENT_TO_PLAN')
        .flatMap((task) => task.data || [])
        .filter(
          (event): event is EventResponse =>
            event !== undefined &&
            'category' in event &&
            event.category !== 'DROPZONE'
        ) ?? [];

    return {
      planningEntityId: planningEntity.planningEntityIdentifier.entityId,
      planEvents,
      planningEntity
    };
  });

  const routeEntities: RouteEntity[] = Object.values(
    planningEntities || {}
  ).filter((entity) => entity.type === 'ROUTE') as RouteEntity[];

  const hasRoutesToPlan = routeEntities.length > 0;

  const scheduleToAssign: ScheduleTask[] =
    routeEntities.length > 0
      ? routeEntities.flatMap(
          (routeEntity) =>
            routeEntity.planningTasks
              ?.filter(
                (planningTask) =>
                  planningTask.category === 'SCHEDULE_TO_ASSIGN' &&
                  planningTask?.data
              )
              .map((planningTask) => ({
                data: planningTask.data
              }))
        )
      : [];

  const { hasActivityPlanningEditAccess } = useSelector(({ grants }) => grants);

  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [uncollapsedCategories, setOpenCategories] = useState<
    TaskListCategory[]
  >([]);

  const categories = Object.values(TaskListCategory).filter(
    (c): c is TaskListCategory => typeof c === 'string'
  ) as TaskListCategory[];
  const allCategoriesAreUncollapsed =
    uncollapsedCategories.length === categories.length;

  const handleClick = (category: TaskListCategory) => {
    setOpenCategories(
      uncollapsedCategories.includes(category)
        ? uncollapsedCategories.filter((c) => c !== category)
        : [...uncollapsedCategories, category]
    );
  };

  const handleSingleOpenCategory = (category: TaskListCategory) => {
    if (uncollapsedCategories.length > 1) return setOpenCategories([category]);

    setOpenCategories((prevCategories) => {
      if (prevCategories.includes(category)) return [];
      return [category];
    });
  };

  const collapseAllCategories = () => {
    setOpenCategories(allCategoriesAreUncollapsed ? [] : categories);
  };

  const handleDrop = () => {
    setOpenCategories((prevCategories) => {
      if (!prevCategories.includes(TaskListCategory.TO_PLAN_APPOINTMENTS)) {
        return [...prevCategories, TaskListCategory.TO_PLAN_APPOINTMENTS];
      }
      return prevCategories;
    });
  };

  useEffect(() => {
    isOpen ? onOpen?.() : onClose?.();
  }, [isOpen]);

  const getCategoryColor = (category: TaskListCategory): string => {
    const categoryString = TaskListCategory[TaskListCategory[category]];

    switch (categoryString) {
      case TaskListCategory.TO_PLAN_APPOINTMENTS:
        return NEW_GREEN_LIGHT;
      case TaskListCategory.SCHEDULE_TO_ASSIGN:
        return ORANGE_LIGHT;
      default:
        return 'transparent';
    }
  };

  const amountOfPlanningEntities = planEventsPerEntity.reduce(
    (acc, { planEvents }) =>
      acc +
      planEvents.filter((item) => (item?.category as string) !== 'DROPZONE')
        .length,
    0
  );

  const amountOfScheduleToAssign = scheduleToAssign?.length;
  const totalTaskListItems =
    (amountOfPlanningEntities || 0) + (amountOfScheduleToAssign || 0);

  const amountOfToPlan = planEventsPerEntity.reduce(
    (acc, { planEvents }) => acc + planEvents.length,
    0
  );

  return (
    <ToPlanDropzone dropzonePadding={dropzonePadding} handleDrop={handleDrop}>
      <List
        aria-labelledby="nested-list"
        subheader={
          <Header>
            <Box
              sx={{
                display: 'flex',
                gap: '8px'
              }}
            >
              <Typography
                variant="h1"
                component="h3"
                lineHeight="28px"
                sx={{ fontFamily: 'signikalight!important', fontSize: '18px' }}
              >
                {t('taskList.title')} ({totalTaskListItems})
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {amountOfToPlan > 0 && (
                  <CategoryBadgeWithTooltip
                    category={TaskListCategory.TO_PLAN_APPOINTMENTS}
                    count={amountOfToPlan ?? 0}
                    getCategoryColor={getCategoryColor}
                    handleSingleOpenCategory={() =>
                      handleSingleOpenCategory(
                        TaskListCategory.TO_PLAN_APPOINTMENTS
                      )
                    }
                    label={t(
                      `taskList.categories.${TaskListCategory.TO_PLAN_APPOINTMENTS}`
                    )}
                  />
                )}
                {amountOfScheduleToAssign > 0 && (
                  <CategoryBadgeWithTooltip
                    category={TaskListCategory.SCHEDULE_TO_ASSIGN}
                    count={amountOfScheduleToAssign ?? 0}
                    getCategoryColor={getCategoryColor}
                    handleSingleOpenCategory={() =>
                      handleSingleOpenCategory(
                        TaskListCategory.SCHEDULE_TO_ASSIGN
                      )
                    }
                    label={t(
                      `taskList.categories.${TaskListCategory.SCHEDULE_TO_ASSIGN}`
                    )}
                  />
                )}
              </Box>
            </Box>
            <IconButton
              onClick={() => setIsOpen(!isOpen)}
              sx={{
                right: '16px',
                bgcolor: isOpen ? 'inherit' : rgbaPrimary(0.1)
              }}
            >
              <ListItemIcon>
                <FontAwesomeSvgIcon
                  icon={isOpen ? faChevronDoubleRight : faChevronDoubleLeft}
                  fontSize="small"
                />
              </ListItemIcon>
            </IconButton>
          </Header>
        }
        sx={{
          position: 'relative',
          zIndex: 40,
          width: TASK_LIST_WIDTH,
          right: isOpen ? 0 : `-${TASK_LIST_WIDTH}px`,
          transitionDuration: '250ms',
          backgroundColor: 'transparent',
          padding: '16px'
        }}
      >
        <TaskListActionBar
          collapseAllCategories={collapseAllCategories}
          allCategoriesAreUncollapsed={allCategoriesAreUncollapsed}
        />

        <TaskListWrapper
          categories={categories}
          uncollapsedCategories={uncollapsedCategories}
          planEventsPerEntity={planEventsPerEntity}
          scheduleToAssign={scheduleToAssign}
          getCategoryColor={getCategoryColor}
          handleClick={handleClick}
          activeEntityContainingSelection={activeEntityContainingSelection}
          onChangeActiveEntity={onChangeActiveEntity}
          onChangeSelection={onChangeSelection}
          hasActivityPlanningEditAccess={hasActivityPlanningEditAccess}
          onChangeMousePosition={onChangeMousePosition}
          hasRoutesToPlan={hasRoutesToPlan}
          onRouteClick={onRouteClick}
          isTimeline={isTimeline}
          amountOfToPlan={amountOfToPlan}
        />
      </List>
    </ToPlanDropzone>
  );
};

export default TaskList;
